import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Eye, EyeOff, ShieldCheck, Hexagon, KeyRound, ScanLine, HelpCircle, X, Building2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Toaster } from "../../components/ui/toaster";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { forgotPasswordInitiation, forgotPasswordConfirm } from "../../lib/api";
import { IDLE_LIMIT_MINUTES } from "../../lib/idleSession";
import { isSsoEnabled, startSsoLogin } from "../../lib/sso";

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  rememberDevice: z.boolean().optional(),
});

const newPasswordSchema = z.object({
  newPassword: z.string().min(8, "Minimum 8 characters"),
  // confirmPassword: z.string().min(1, "Please confirm your password"),
});
// .refine((d) => d.newPassword === d.confirmPassword, {
//   message: "Passwords do not match",
//   path: ["confirmPassword"],
// });

const mfaSchema = z.object({
  otpCode: z.string().length(6, "Enter the 6-digit code"),
});

/**
 * Normalise the challenge name out of a sign-in response and map it to the step
 * the form should advance to. Covers every known field name and casing variant
 * the backend has returned. Returns null for anything unrecognised.
 */
function classifyChallenge(result) {
  const raw =
    result?.nextStep ||
    result?.ChallengeName ||
    result?.challengeName ||
    result?.challenge ||
    result?.status ||
    "";
  const challenge = String(raw).toUpperCase();

  if (challenge.includes("NEW_PASSWORD") || challenge.includes("FORCE_CHANGE")) return "new_password";
  if (challenge.includes("TOTP_SETUP") || challenge.includes("MFA_SETUP")) return "totp_setup";
  if (
    challenge.includes("TOTP_CODE") ||
    challenge.includes("SOFTWARE_TOKEN") ||
    challenge.includes("SMS_MFA")
  ) {
    return "mfa";
  }
  return null;
}

// The two /auth/mfa/setup call sites read different key sets in a different
// order of preference, and the orders matter: qr_code_url may be an image URL
// rather than an otpauth:// URI, so a path that prefers setupUri must keep
// preferring it. These are kept separate rather than merged for that reason.
const SIGNIN_TOTP_KEYS = {
  secret: ["secretCode", "SecretCode", "secret"],
  uri: ["setupUri", "qrCodeUri", "otpUri"],
};
const NEW_PASSWORD_TOTP_KEYS = {
  secret: ["secret_code", "secretCode", "SecretCode"],
  uri: ["qr_code_url", "qrCodeUrl", "setupUri", "qrCodeUri"],
};

function firstValue(obj, keys) {
  for (const key of keys) {
    if (obj?.[key]) return obj[key];
  }
  return "";
}

/**
 * Pull the TOTP secret and QR value out of a /auth/mfa/setup response.
 *
 * `email` is only supplied on the sign-in path; when present it becomes the
 * account label in the fallback otpauth:// URI.
 */
function buildTotpSetup(setupData, keys, email) {
  const secret = firstValue(setupData, keys.secret);
  const account = email ? `:${encodeURIComponent(email)}` : "";
  const uri =
    firstValue(setupData, keys.uri) ||
    `otpauth://totp/TETRA%20MICRO%20INFUSION%20SYSTEM${account}?secret=${secret}&issuer=TETRA`;
  return { secret, uri };
}

const SESSION_EXPIRED_KEY = "session_expired";
const SESSION_END_REASON_KEY = "session_end_reason";

/**
 * Whether to show the session-expired banner: either the ?expired=1 query flag
 * or this tab's sessionStorage marker. Also clears a stale localStorage marker,
 * so the banner stays scoped to the tab that actually timed out.
 */
function readExpiredFlag(search) {
  const fromQuery = new URLSearchParams(search).get("expired") === "1";
  const fromStorage = sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
  if (!fromStorage) localStorage.removeItem(SESSION_EXPIRED_KEY);
  return fromQuery || fromStorage;
}

/** True when the session that just ended was ended by the 60-minute idle timeout (FMP-AU-005). */
function readEndedIdle() {
  try {
    return sessionStorage.getItem(SESSION_END_REASON_KEY) === "idle";
  } catch {
    return false;
  }
}

export default function LoginPage() {
  const { mfaLogin, setNewPasswordApi, confirmMfaSignIn, fetchMfaSetup, verifyMfaSetup, webSignIn, sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Always redirect to dashboard after login
  const from = "/";

  const { toast } = useToast();

  // Show the session-expired banner only for the current tab/session.
  const [showExpiredBanner, setShowExpiredBanner] = useState(() => readExpiredFlag(location.search));
  // An idle timeout is reported as a toast instead, so the user gets one message
  // rather than two saying the same thing.
  const [endedIdle] = useState(readEndedIdle);
  const isExpired = (sessionExpired || showExpiredBanner) && !endedIdle;
  const dismissExpired = () => { clearSessionExpired(); setShowExpiredBanner(false); };

  useEffect(() => {
    if (!isExpired) return undefined;
    const timer = setTimeout(() => {
      dismissExpired();
    }, 5000);

    return () => clearTimeout(timer);
  }, [isExpired]);

  // FMP-AU-005: tell the user why they are back at the login screen. The marker
  // is cleared as it is read, so the toast appears once and does not come back on
  // a re-render or a reload.
  useEffect(() => {
    if (!endedIdle) return;
    toast({
      // Reads the configured limit rather than a fixed 60, so a build running a
      // shortened testing limit does not tell the user something untrue.
      title: `Signed out after ${IDLE_LIMIT_MINUTES} minutes of inactivity`,
      description: "For your security your session was ended. Please sign in again to continue.",
      variant: "warning",
      duration: 8000,
    });
    clearSessionExpired();
    try { sessionStorage.removeItem(SESSION_END_REASON_KEY); } catch { /* storage unavailable */ }
  }, [endedIdle, toast, clearSessionExpired]);

  // "login" | "new_password" | "totp_setup" | "mfa"
  const [step, setStep] = useState("login");
  // "sso" | "standard"
  // NOTE: despite the name, "sso" here is the email + password + MFA flow (the
  // "MFA Sign In" tab). Real enterprise SSO is the separate federated button
  // below both forms — see ssoRedirecting / onSsoLogin.
  const [loginMode, setLoginMode] = useState("sso");
  const [serverError, setServerError] = useState(null);
  // Enterprise SSO (FMP-AU-004). Separate from the form state above because the
  // federated path shares no fields with either password form.
  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [totpSetupUri, setTotpSetupUri] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [showMfaHelp, setShowMfaHelp] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState(null); // null | "loading" | "success" | "error"
  const [forgotError, setForgotError] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmStatus, setForgotConfirmStatus] = useState(null); // null | "loading" | "done"
  const [forgotConfirmError, setForgotConfirmError] = useState(null);

  // Hand off to the Hosted UI → IdP. On success this navigates away, so the
  // spinner is only ever cleared on failure.
  const onSsoLogin = async () => {
    setServerError(null);
    setSsoRedirecting(true);
    try {
      await startSsoLogin();
    } catch (err) {
      console.error("[Auth] SSO redirect failed:", err);
      setServerError(
        "Could not start single sign-on. Please try again, or sign in with your email and password."
      );
      setSsoRedirecting(false);
    }
  };

  const openForgotModal = () => {
    setForgotEmail("");
    setForgotStatus(null);
    setForgotError(null);
    setForgotCode("");
    setForgotNewPassword("");
    setForgotConfirmStatus(null);
    setForgotConfirmError(null);
    setShowForgotModal(true);
  };

  const handleForgotConfirmSubmit = async (e) => {
    e.preventDefault();
    if (!forgotCode || !forgotNewPassword) return;
    setForgotConfirmStatus("loading");
    setForgotConfirmError(null);
    try {
      await forgotPasswordConfirm(forgotEmail, forgotCode, forgotNewPassword);
      setForgotConfirmStatus("done");
    } catch (err) {
      setForgotConfirmError(err.message || "Failed to reset password. Please try again.");
      setForgotConfirmStatus(null);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotStatus("loading");
    setForgotError(null);
    try {
      await forgotPasswordInitiation(forgotEmail);
      setForgotStatus("success");
    } catch (err) {
      setForgotError(err.message || "Failed to initiate password reset. Please try again.");
      setForgotStatus("error");
    }
  };

  const loginForm = useForm({ resolver: zodResolver(schema), defaultValues: { rememberDevice: true } });
  const newPasswordForm = useForm({ resolver: zodResolver(newPasswordSchema) });
  const mfaForm = useForm({ resolver: zodResolver(mfaSchema) });

  const onStandardLoginSubmit = async (data) => {
    setServerError(null);
    try {
      const result = await webSignIn(data.email, data.password);
      if (result?.isSignedIn) {
        navigate(from, { replace: true });
      } else {
        setServerError("Sign-in failed. Please check your credentials and try again.");
      }
    } catch (err) {
      setServerError(err?.message || "Sign-in failed. Check your credentials and try again.");
    }
  };

  // Fetch the QR code / secret and stage it for the totp_setup step.
  // Returns false (after setting the error banner) if the setup call fails.
  const loadTotpSetup = async (keys, email) => {
    try {
      const setupData = await fetchMfaSetup();
      const { secret, uri } = buildTotpSetup(setupData, keys, email);
      setTotpSecret(secret);
      setTotpSetupUri(uri);
      return true;
    } catch (setupErr) {
      setServerError(setupErr?.message || "Failed to load MFA setup. Please try again.");
      return false;
    }
  };

  const onLoginSubmit = async (data) => {
    console.log('[LoginPage] onLoginSubmit called', data.email);
    setServerError(null);
    try {
      const result = await mfaLogin(data.email, data.password);
      console.log('[LoginPage] mfaLogin result:', result);

      // Already signed in — no challenge issued.
      if (result?.isSignedIn) {
        navigate(from, { replace: true });
        return;
      }

      const nextStep = classifyChallenge(result);

      if (nextStep === "totp_setup") {
        // Fetch QR code from the setup endpoint.
        const loaded = await loadTotpSetup(SIGNIN_TOTP_KEYS, data.email);
        if (!loaded) return;
        setStep("totp_setup");
      } else if (nextStep) {
        setStep(nextStep);
      } else {
        console.warn('[LoginPage] Unhandled mfaLogin result:', result);
        setServerError("Unexpected response from server. Please try again.");
      }
    } catch (err) {
      setServerError(err?.message || "Sign-in failed. Check your credentials and try again.");
    }
  };

  const onNewPasswordSubmit = async (data) => {
    setServerError(null);
    try {
      // Call POST /auth/new-password — stores access_token internally for MFA steps.
      const result = await setNewPasswordApi(data.newPassword);
      console.log('[LoginPage] setNewPassword result:', result);

      const nextStep = String(result?.next_step || result?.nextStep || result?.ChallengeName || "").toUpperCase();

      if (nextStep.includes("MFA_SETUP")) {
        // Call POST /auth/mfa/setup (Bearer access_token) to get QR code.
        // No email here: this path's fallback URI carries no account label.
        const loaded = await loadTotpSetup(NEW_PASSWORD_TOTP_KEYS, null);
        if (!loaded) return;
        setStep("totp_setup");
      } else if (result?.isSignedIn) {
        navigate(from, { replace: true });
      } else {
        console.warn('[LoginPage] Unhandled setNewPassword result:', result);
        setServerError("Unexpected response. Please try again.");
      }
    } catch (err) {
      setServerError(err?.message || "Failed to set new password. Please try again.");
    }
  };

  const onMfaSubmit = async (data) => {
    setServerError(null);
    try {
      if (step === "totp_setup") {
        await verifyMfaSetup(data.otpCode);
      } else {
        await confirmMfaSignIn(data.otpCode);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setServerError(err?.message || "Invalid code. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">

      {/* The authenticated shell renders its own Toaster, but this page sits
          outside it — without this the idle-timeout toast would have nowhere to
          appear. */}
      <Toaster />

      {/* ── Session Expired Banner ── */}
      {isExpired && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg">
            <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Your session has expired</p>
              {/* Inactivity is reported by its own toast; this banner covers every
                  other way a session ends, so it no longer claims a cause it
                  cannot know. */}
              <p className="text-sm text-amber-700">For your security, you have been signed out. Please sign in again to continue.</p>
            </div>
            <button
              type="button"
              onClick={dismissExpired}
              className="ml-2 text-amber-500 hover:text-amber-700"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Forgot Password Modal ── */}
      {showForgotModal && (
        <ForgotPasswordModal
          forgotEmail={forgotEmail}
          setForgotEmail={setForgotEmail}
          forgotStatus={forgotStatus}
          forgotError={forgotError}
          setShowForgotModal={setShowForgotModal}
          forgotCode={forgotCode}
          setForgotCode={setForgotCode}
          forgotNewPassword={forgotNewPassword}
          setForgotNewPassword={setForgotNewPassword}
          forgotConfirmStatus={forgotConfirmStatus}
          forgotConfirmError={forgotConfirmError}
          handleForgotSubmit={handleForgotSubmit}
          handleForgotConfirmSubmit={handleForgotConfirmSubmit}
        />
      )}


      {/* Brand */}
      <div className="text-center mb-5">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#08148C] text-teal-300 mb-3 shadow-lg">
          <Hexagon className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-bold text-[#060f6e] tracking-tight">
          TETRA MICRO INFUSION SYSTEM
        </h1>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mt-1">
          Fleet Management Portal
        </p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-xl shadow-md px-8 py-7">

        {/* ── STEP: Login ─────────────────────────────────────────────────── */}
        {step === "login" && (
          <>
            {/* Mode toggle */}
            <div className="flex rounded-lg border border-teal-200 p-0.5 mb-6 bg-slate-50">
              <button
                type="button"
                onClick={() => { setLoginMode("sso"); setServerError(null); loginForm.reset(); }}
                className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-all duration-150 ${
                  loginMode === "sso"
                    ? "bg-[#08148C] text-white shadow-sm"
                    : "text-slate-500 hover:bg-teal-50 hover:text-[#08148C]"
                }`}
              >
                MFA Sign In
              </button>
              <button
                type="button"
                onClick={() => { setLoginMode("standard"); setServerError(null); loginForm.reset(); }}
                className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-all duration-150 ${
                  loginMode === "standard"
                    ? "bg-[#08148C] text-white shadow-sm"
                    : "text-slate-500 hover:bg-teal-50 hover:text-[#08148C]"
                }`}
              >
                Standard Sign In
              </button>
            </div>

            {/* ── Standard (plain username/password) form ── */}
            {loginMode === "standard" && (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-6">Sign in to your account</h2>

                {serverError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{serverError}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={loginForm.handleSubmit(onStandardLoginSubmit)} noValidate className="space-y-4">
                  <div className="space-y-1">
                    <Label htmlFor="std-email" className="text-sm font-medium text-slate-700">
                      Email address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="std-email"
                      type="email"
                      autoComplete="email"
                      placeholder="user@example.com"
                      {...loginForm.register("email")}
                      aria-invalid={!!loginForm.formState.errors.email}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-xs text-red-500">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="std-password" className="text-sm font-medium text-slate-700">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="std-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        className="pr-10"
                        {...loginForm.register("password")}
                        aria-invalid={!!loginForm.formState.errors.password}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-xs text-red-500">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button type="button" className="text-sm text-[#08148C] hover:underline" onClick={openForgotModal}>
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loginForm.formState.isSubmitting}
                    className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
                  >
                    {loginForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Sign In
                  </button>
                </form>
              </>
            )}

            {/* ── SSO form (existing — unchanged) ── */}
            {loginMode === "sso" && (
              <>
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Sign in to your account</h2>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="user@example.com"
                  {...loginForm.register("email")}
                  aria-invalid={!!loginForm.formState.errors.email}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className="pr-10"
                    {...loginForm.register("password")}
                    aria-invalid={!!loginForm.formState.errors.password}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="remember-device" className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input
                    id="remember-device"
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 accent-[#08148C]"
                    {...loginForm.register("rememberDevice")}
                  />{" "}
                  Remember this device
                </label>
                <button type="button" className="text-sm text-[#08148C] hover:underline" onClick={openForgotModal}>
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loginForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {loginForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Sign In
              </button>
            </form>

              </>
            )}

            {/* ── Enterprise SSO (FMP-AU-004) ──────────────────────────────────
                Sits below the Sign In button of whichever form is active, so it
                reads as an alternative to password sign-in rather than a third
                tab. Hidden entirely unless a Hosted UI domain AND an identity
                provider are configured — a button that dead-ends on a Cognito
                error page is worse than no button. Label stays vendor-neutral
                so swapping the IdP needs no copy change. */}
            {isSsoEnabled() && (
              <div className="mt-5">
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-2 text-xs uppercase tracking-wide text-slate-400">
                      or
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onSsoLogin}
                  disabled={ssoRedirecting}
                  className="w-full flex items-center justify-center gap-2 border border-[#08148C] bg-white hover:bg-blue-50 disabled:opacity-60 text-[#08148C] font-medium py-2.5 rounded-lg transition-colors text-sm"
                >
                  {ssoRedirecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Building2 className="h-4 w-4" />
                  )}
                  Sign in with SAML / SSO
                </button>

                <p className="mt-2 text-center text-xs text-slate-400">
                  Uses your organisation's identity provider. No password is stored here.
                </p>
              </div>
            )}

            {/* MFA Help Button — only shown on MFA tab */}
            {loginMode === "sso" && (
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowMfaHelp((v) => !v)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#08148C] transition-colors"
                  aria-label="MFA Sign In?"
                >
                  <HelpCircle className="h-4 w-4" />
                  MFA Sign In
                </button>
              </div>
            )}

            {/* MFA Help Panel */}
            {loginMode === "sso" && showMfaHelp && (
              <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-4 relative text-sm text-slate-700">
                <button
                  type="button"
                  onClick={() => setShowMfaHelp(false)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <p className="font-semibold text-[#08148C] mb-1">Multi-Factor Authentication (MFA)</p>
                <p className="text-xs text-slate-600 leading-relaxed mb-2">
                  MFA adds a second layer of security to your account. After entering your password, you will be prompted
                  to provide a one-time code from an authenticator app (e.g. Google Authenticator or Authy).
                </p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                  <li>Protects your account even if your password is compromised.</li>
                  <li>The 6-digit code refreshes every 30 seconds.</li>
                  <li>First-time users will be guided to set up the authenticator app.</li>
                </ul>
              </div>
            )}

          </>
        )}

        {/* ── STEP: Set New Password ───────────────────────────────────────── */}
        {step === "new_password" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <KeyRound className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Set a new password</h2>
                <p className="text-xs text-slate-400">Your temporary password has expired. Please set a permanent password.</p>
              </div>
            </div>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={newPasswordForm.handleSubmit(onNewPasswordSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">
                  New password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    className="pr-10"
                    {...newPasswordForm.register("newPassword")}
                    aria-invalid={!!newPasswordForm.formState.errors.newPassword}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowNewPassword((v) => !v)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPasswordForm.formState.errors.newPassword && (
                  <p className="text-xs text-red-500">{newPasswordForm.formState.errors.newPassword.message}</p>
                )}
                <p className="text-xs text-slate-400">Min 8 characters &middot; uppercase &middot; number &middot; special character</p>
              </div>

              {/* <div className="space-y-1">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                  Confirm password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  {...newPasswordForm.register("confirmPassword")}
                  aria-invalid={!!newPasswordForm.formState.errors.confirmPassword}
                />
                {newPasswordForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500">{newPasswordForm.formState.errors.confirmPassword.message}</p>
                )}
              </div> */}

              <button
                type="submit"
                disabled={newPasswordForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {newPasswordForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Set Password &amp; Continue
              </button>
            </form>
          </>
        )}

        {/* ── STEP: TOTP Setup (scan QR code) ─────────────────────────────── */}
        {step === "totp_setup" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                <ScanLine className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Set up Authenticator App</h2>
                <p className="text-xs text-slate-400">One-time setup required before you can sign in.</p>
              </div>
            </div>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <ol className="text-sm text-slate-600 space-y-1.5 mb-4 list-decimal list-inside">
              <li>Install <strong>Google Authenticator</strong> or <strong>Authy</strong> on your phone</li>
              <li>Tap <strong>+</strong> → <strong>Scan a QR code</strong></li>
              <li>Scan the code below</li>
              <li>Enter the 6-digit code shown in the app</li>
            </ol>

            {totpSetupUri && (
              <div className="flex flex-col items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
                {/* Render as <img> for image URLs, or generate QR locally for otpauth:// URIs */}
                {totpSetupUri.startsWith("otpauth://") ? (
                  <QRCodeSVG value={totpSetupUri} size={180} level="M" />
                ) : (
                  <img
                    src={totpSetupUri}
                    alt="MFA QR Code"
                    className="w-[180px] h-[180px] object-contain"
                  />
                )}
                {totpSecret && (
                  <>
                    <p className="text-[11px] text-slate-400 text-center">Can't scan? Enter this key manually:</p>
                    <code className="text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1 text-slate-700 break-all text-center select-all">
                      {totpSecret}
                    </code>
                  </>
                )}
              </div>
            )}

            <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="otpCodeSetup" className="text-sm font-medium text-slate-700">
                  6-digit code from app <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="otpCodeSetup"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  className="text-center text-lg tracking-widest font-mono"
                  {...mfaForm.register("otpCode")}
                  aria-invalid={!!mfaForm.formState.errors.otpCode}
                />
                {mfaForm.formState.errors.otpCode && (
                  <p className="text-xs text-red-500">{mfaForm.formState.errors.otpCode.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={mfaForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {mfaForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Verify &amp; Complete Setup
              </button>
            </form>
          </>
        )}

        {/* ── STEP: MFA OTP ────────────────────────────────────────────────── */}
        {step === "mfa" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Verify your identity</h2>
                <p className="text-xs text-slate-400">Enter the 6-digit code sent to your registered device.</p>
              </div>
            </div>

            {serverError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)} noValidate className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="otpCode" className="text-sm font-medium text-slate-700">
                  One-time code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  className="text-center text-lg tracking-widest font-mono"
                  {...mfaForm.register("otpCode")}
                  aria-invalid={!!mfaForm.formState.errors.otpCode}
                />
                {mfaForm.formState.errors.otpCode && (
                  <p className="text-xs text-red-500">{mfaForm.formState.errors.otpCode.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={mfaForm.formState.isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {mfaForm.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Verify &amp; Sign In
              </button>

              <button
                type="button"
                onClick={() => { setStep("login"); setServerError(null); }}
                className="w-full text-sm text-slate-500 hover:text-slate-800 text-center"
              >
                &#8592; Back to sign in
              </button>
            </form>
          </>
        )}
      </div>

    </div>
  );
}


/**
 * Password-reset dialog. Extracted from LoginPage so the two-step reset flow's
 * conditional rendering is scored on its own rather than inflating the login
 * form's cognitive complexity (S3776).
 */
function ForgotPasswordModal({
  forgotEmail,
  setForgotEmail,
  forgotStatus,
  forgotError,
  setShowForgotModal,
  forgotCode,
  setForgotCode,
  forgotNewPassword,
  setForgotNewPassword,
  forgotConfirmStatus,
  forgotConfirmError,
  handleForgotSubmit,
  handleForgotConfirmSubmit,
}) {
  return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Reset Password</h3>
              <button
                type="button"
                onClick={() => setShowForgotModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {forgotStatus === "success" && forgotConfirmStatus !== "done" && (
              <form onSubmit={handleForgotConfirmSubmit} noValidate className="space-y-4">
                <p className="text-sm text-slate-500">
                  A verification code has been sent to <span className="font-medium">{forgotEmail}</span>. Enter the code and your new password below.
                </p>
                <div className="space-y-1">
                  <Label htmlFor="forgot-code" className="text-sm font-medium text-slate-700">
                    Verification code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="forgot-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={10}
                    value={forgotCode}
                    onChange={(e) => setForgotCode(e.target.value.trim())}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="forgot-new-password" className="text-sm font-medium text-slate-700">
                    New password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="forgot-new-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="New secure password"
                    value={forgotNewPassword}
                    onChange={(e) => setForgotNewPassword(e.target.value)}
                    required
                  />
                </div>
                {forgotConfirmError && (
                  <Alert variant="destructive">
                    <AlertDescription>{forgotConfirmError}</AlertDescription>
                  </Alert>
                )}
                <button
                  type="submit"
                  disabled={forgotConfirmStatus === "loading" || !forgotCode || !forgotNewPassword}
                  className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
                >
                  {forgotConfirmStatus === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Reset Password
                </button>
              </form>
            )}
            {forgotConfirmStatus === "done" && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                Password has been reset successfully. Please sign in.
              </div>
            )}
            {forgotConfirmStatus !== "done" && forgotStatus !== "success" && (
              <form onSubmit={handleForgotSubmit} noValidate className="space-y-4">
                <p className="text-sm text-slate-500">Enter your registered email address and we will send you a reset link.</p>
                <div className="space-y-1">
                  <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-700">
                    Email address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="user@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                {forgotError && (
                  <Alert variant="destructive">
                    <AlertDescription>{forgotError}</AlertDescription>
                  </Alert>
                )}
                <button
                  type="submit"
                  disabled={forgotStatus === "loading" || !forgotEmail}
                  className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
                >
                  {forgotStatus === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Send Reset Link
                </button>
              </form>
            )}
          </div>
        </div>
  );
}